/**
 * Application Entry Point
 *
 * Sets up the React application with:
 * - React StrictMode for development checks
 * - React Query for server state management
 * - Query client with one automatic retry
 * - DevTools for development environment
 */

import { Fragment, StrictMode, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './App.tsx';
import { QUERY_RETRY_COUNT } from './lib/queryRetryPolicy';
const DesktopPopup = lazy(() => import('./components/popups/DesktopPopup'));

// Lazy load devtools - only fetched in development, zero overhead in production
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({
    default: m.ReactQueryDevtools,
  }))
);

/**
 * Create Query Client with default configuration
 *
 * Features:
 * - Automatic retry: one retry after a failed query
 * - Retry delay: Exponential backoff starting at 1 second
 * - Stale time: 5 minutes (data considered fresh)
 * - Cache time: 10 minutes (unused data kept in cache)
 * - Refetch on window focus: Enabled for data freshness
 * - Refetch on reconnect: Enabled for offline recovery
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep query-level retries bounded because the API client may also retry.
      retry: QUERY_RETRY_COUNT,

      // Exponential backoff starting at 1 second
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Consider data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Refetch on window focus to ensure data freshness
      refetchOnWindowFocus: true,

      // Refetch on network reconnect for offline recovery
      refetchOnReconnect: true,

      // Don't refetch on component mount if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      // Mutations must NOT auto-retry by default. Re-invoking a non-idempotent
      // mutationFn risks a double-submit (double charge / credit / order). Opt
      // specific idempotent mutations into retry explicitly via a per-hook `retry`.
      // (Payment/order paths use raw storeApi calls with skipRetry, not useMutation,
      // and the axios interceptor still provides transient-error resilience.)
      retry: false,
    },
  },
});

// Disable StrictMode during local profiling to avoid duplicate mount/fetch noise in dev.
const RootWrapper = import.meta.env.DEV ? Fragment : StrictMode;

// Build-generated tags make every public URL useful to crawlers before the
// JavaScript bundle runs. Once React starts, remove only those initial tags so
// SEOHead owns a single canonical/description/robots set during SPA navigation.
document.querySelectorAll('[data-seo-static="true"]').forEach((element) => {
  element.remove();
});

createRoot(document.getElementById('root')!).render(
  <RootWrapper>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {window.location.pathname === '/desktop-popup'
          ? <Suspense fallback={null}><DesktopPopup /></Suspense>
          : <App />}
        {/* DevTools lazy loaded and only rendered in development */}
        {import.meta.env.DEV && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </QueryClientProvider>
    </HelmetProvider>
  </RootWrapper>
);
